/**
 * services/ai/AnthropicProvider.ts
 * Anthropic provider implementation
 *
 * Implements the `AIProvider` interface for Anthropic's messages API.
 * All requests are routed through the ai-proxy Edge Function because
 * Anthropic blocks browser CORS. Uses raw `fetch()` — no SDK dependency.
 *
 * Features:
 * - Non-streaming chat via proxy with `stream: false`
 * - Streaming chat via proxy SSE with `stream: true`
 * - API key validation via minimal proxy call (`max_tokens: 1`)
 * - System message extraction (Anthropic uses top-level `system` field)
 * - JWT authentication for proxy access
 *
 * Work Unit: WU04 - AI Provider Client Implementations
 * Task: T02 - Anthropic Provider Client
 * Sprint: S01 - AI Architecture & Infrastructure Foundation
 * Project: P09 - AI Integration
 */

// Internal absolute imports (type-only)
import type {
  AIChatOptions,
  AIKeyValidationResult,
  AIMessage,
  AIProvider,
  AIResponse,
  AIStreamChunk,
} from '@/types/shared/AI';

// Internal absolute imports (value)
import { supabase } from '@/lib/supabase';
import { createSSEIterable } from '@/utils/sseParser';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Default model when none specified in options */
const DEFAULT_MODEL = 'claude-sonnet-4-5-20250929';

/** Default max tokens (required by Anthropic API) */
const DEFAULT_MAX_TOKENS = 1024;

// ─── Provider ───────────────────────────────────────────────────────────────

/**
 * Anthropic provider implementation
 *
 * Routes all requests through the ai-proxy Edge Function because
 * Anthropic's API does not support browser CORS. The proxy handles
 * header translation and SSE passthrough.
 *
 * @example
 * ```ts
 * const provider = new AnthropicProvider('sk-ant-...');
 * const response = await provider.chat([{ role: 'user', content: 'Hello' }]);
 * console.log(response.content);
 * ```
 */
export class AnthropicProvider implements AIProvider {
  /** Provider identifier */
  readonly provider = 'anthropic' as const;

  /** API key for authentication */
  private readonly apiKey: string;

  /**
   * Create a new Anthropic provider instance
   *
   * @param apiKey - Anthropic API key (e.g., 'sk-ant-...')
   */
  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  /**
   * Send a non-streaming chat completion request
   *
   * Posts to the ai-proxy Edge Function with `stream: false` and
   * normalizes the Anthropic response into the provider-agnostic
   * `AIResponse` shape.
   *
   * @param messages - Conversation messages
   * @param options - Optional chat parameters (model, maxTokens, temperature, systemPrompt)
   * @returns Normalized AI response
   * @throws Error on network failure, missing session, or non-OK HTTP status
   */
  async chat(messages: AIMessage[], options?: AIChatOptions): Promise<AIResponse> {
    const token = await this.getSessionToken();
    const proxyUrl = this.getProxyUrl();
    const body = this.buildProxyBody(messages, options, false);

    const response = await fetch(proxyUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response));
    }

    const data = await response.json() as AnthropicChatResponse;

    return {
      content: data.content[0]?.text ?? '',
      model: data.model,
      usage: {
        promptTokens: data.usage.input_tokens,
        completionTokens: data.usage.output_tokens,
        totalTokens: data.usage.input_tokens + data.usage.output_tokens,
      },
      finishReason: data.stop_reason ?? 'unknown',
    };
  }

  /**
   * Send a streaming chat completion request
   *
   * Posts to the ai-proxy Edge Function with `stream: true` and yields
   * `AIStreamChunk` objects as Anthropic typed SSE events arrive. The
   * final chunk has `isComplete: true` when `message_stop` is received.
   *
   * @param messages - Conversation messages
   * @param options - Optional chat parameters (model, maxTokens, temperature, systemPrompt)
   * @returns Async iterable of stream chunks
   * @throws Error on network failure, missing session, or non-OK HTTP status
   */
  async *chatStream(
    messages: AIMessage[],
    options?: AIChatOptions,
  ): AsyncGenerator<AIStreamChunk, void, undefined> {
    const token = await this.getSessionToken();
    const proxyUrl = this.getProxyUrl();
    const body = this.buildProxyBody(messages, options, true);

    const response = await fetch(proxyUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response));
    }

    for await (const event of createSSEIterable(response)) {
      // Dispatch on Anthropic typed event
      if (event.event === 'content_block_delta') {
        const chunk = JSON.parse(event.data) as AnthropicContentBlockDelta;
        yield {
          content: chunk.delta.text ?? '',
          isComplete: false,
        };
      } else if (event.event === 'message_stop') {
        yield { content: '', isComplete: true };
        return;
      }
      // Ignore other event types (message_start, content_block_start, message_delta, ping)
    }
  }

  /**
   * Validate an API key against Anthropic via the proxy
   *
   * Makes a minimal messages call (`max_tokens: 1`) through the proxy
   * to verify the key is valid without significant cost.
   *
   * @param apiKey - Plaintext API key to validate
   * @returns Validation result
   */
  async validateKey(apiKey: string): Promise<AIKeyValidationResult> {
    try {
      const token = await this.getSessionToken();
      const proxyUrl = this.getProxyUrl();

      const body = {
        provider: 'anthropic',
        apiKey,
        model: DEFAULT_MODEL,
        messages: [{ role: 'user', content: 'Hi' }],
        maxTokens: 1,
        stream: false,
      };

      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        return { valid: true };
      }

      if (response.status === 403) {
        // Proxy auth failure (JWT expired/invalid) — unambiguous via 403
        return { valid: false, code: 'session_expired', error: 'Session expired — please sign in again.' };
      }

      if (response.status === 401) {
        // Anthropic API key rejection (passthrough from upstream)
        return { valid: false, code: 'invalid_key', error: 'Invalid API key. Please check and re-enter.' };
      }

      if (response.status === 429) {
        return { valid: false, code: 'rate_limited', error: 'Rate limited — please try again in a moment.' };
      }

      const errorText = await response.text().catch(() => 'Unknown error');
      return { valid: false, code: 'server_error', error: `Validation failed (${response.status}): ${errorText}` };
    } catch (err) {
      return {
        valid: false,
        code: 'network_error',
        error: `Network error: ${err instanceof Error ? err.message : 'Check your internet connection and try again.'}`,
      };
    }
  }

  // ─── Private Helpers ────────────────────────────────────────────────────────

  /**
   * Get the current session JWT token for proxy authentication
   *
   * @returns JWT access token
   * @throws Error if no active session exists
   */
  private async getSessionToken(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error('No active session — please sign in to use AI features');
    }

    return session.access_token;
  }

  /**
   * Get the ai-proxy Edge Function URL
   *
   * @returns Proxy endpoint URL
   */
  private getProxyUrl(): string {
    return `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/ai-proxy`;
  }

  /**
   * Build the proxy request body with system message extraction
   *
   * Extracts system-role messages from the messages array and merges
   * them with the optional `systemPrompt` from chat options. Anthropic
   * requires system prompts as a top-level field, not in the messages array.
   *
   * @param messages - User-provided conversation messages
   * @param options - Optional chat parameters
   * @param stream - Whether to enable streaming
   * @returns Proxy request body
   */
  private buildProxyBody(
    messages: AIMessage[],
    options: AIChatOptions | undefined,
    stream: boolean,
  ): Record<string, unknown> {
    const model = options?.model ?? DEFAULT_MODEL;
    const maxTokens = options?.maxTokens ?? DEFAULT_MAX_TOKENS;

    // Extract system messages from the array (Anthropic doesn't accept role: 'system')
    const systemMessages: string[] = [];
    const chatMessages: Array<{ role: string; content: string }> = [];

    for (const msg of messages) {
      if (msg.role === 'system') {
        systemMessages.push(msg.content);
      } else {
        chatMessages.push({ role: msg.role, content: msg.content });
      }
    }

    // Merge extracted system messages with explicit systemPrompt option
    if (options?.systemPrompt != null) {
      systemMessages.push(options.systemPrompt);
    }

    const body: Record<string, unknown> = {
      provider: 'anthropic',
      apiKey: this.apiKey,
      model,
      messages: chatMessages,
      maxTokens,
      stream,
    };

    if (systemMessages.length > 0) {
      body['systemPrompt'] = systemMessages.join('\n\n');
    }

    if (options?.temperature != null) {
      body['temperature'] = options.temperature;
    }

    return body;
  }

  /**
   * Build a human-readable error message from a failed proxy response
   *
   * @param response - Failed fetch Response
   * @returns Error message string
   */
  private async buildErrorMessage(response: Response): Promise<string> {
    const status = response.status;

    if (status === 403) {
      // Proxy auth failure (JWT expired/invalid) — unambiguous via 403
      return 'Session expired — please sign in again';
    }
    if (status === 401) {
      // Anthropic API key rejection (passthrough from upstream)
      return 'Anthropic API key is invalid or expired';
    }
    if (status === 429) {
      return 'Rate limit exceeded — please try again later';
    }
    if (status === 400) {
      const body = await response.text().catch(() => '');
      return `Bad request: ${body}`;
    }
    if (status >= 500) {
      return `Server error (${status}) — please try again later`;
    }

    const body = await response.text().catch(() => '');
    return `Anthropic proxy request failed (${status}): ${body}`;
  }
}

// ─── Anthropic API Response Types (Internal) ─────────────────────────────────

/** Anthropic non-streaming chat response shape */
interface AnthropicChatResponse {
  content: Array<{
    text: string;
  }>;
  model: string;
  stop_reason: string | null;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

/** Anthropic streaming content_block_delta event data shape */
interface AnthropicContentBlockDelta {
  delta: {
    type: string;
    text?: string;
  };
}
